import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Skeleton } from 'antd';

import ChevronIcon from '@assets/icons/certificates/wallet/chevron.svg';
import { Routes } from '@share/constants';
import { isInstalmentWalletPaymentType } from '@share/common-types';
import { walletDrawerActions } from '@share/store/slices';
import { useDispatch } from 'react-redux';

import type { WalletCardProps } from '../../types';
import {
  formatWalletDate,
  getWalletStatusClassName,
  getWalletStatusLabel,
  resolveInstallmentProgressProps,
  shouldShowExpiryOnFullPaymentNote,
  shouldShowWalletUpdatePaymentCard,
} from '../../utils';
import { InstallmentProgress } from '../installment-progress';
import { WalletItem } from '../wallet-item';

import { useWalletCardInstallments } from './hooks';

import styles from './styles.module.scss';

const WalletCardUpdatePaymentBlockLazy = lazy(() => import('./wallet-card-update-payment-block'));

export const WalletCard = ({
  certificate,
  accountUsername,
  fetchCertificates,
}: WalletCardProps): React.ReactElement => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const dispatch = useDispatch();

  const installmentRefetchErrorMessage = useMemo(
    () => intl.formatMessage({ id: 'certificates.wallet.refetchInstallmentsFailed' }),
    [intl],
  );

  const {
    fetchedInstallmentSchedule,
    fetchedCertificateTotalPrice,
    isInstallmentsLoading,
    shouldFetchInstallments,
    refetchInstallments,
  } = useWalletCardInstallments({
    certificateId: certificate.id,
    paymentType: certificate.paymentType,
    status: certificate.status,
    isExpanded,
    installmentRefetchErrorMessage,
  });

  const purchasedText: string = useMemo(
    () => formatWalletDate(certificate.purchasedAt),
    [certificate.purchasedAt],
  );
  const expiresText: string = useMemo(
    () => formatWalletDate(certificate.expiresAt),
    [certificate.expiresAt],
  );

  const detailsPath: string = accountUsername
    ? `/${accountUsername}${Routes.Certificates}/${certificate.id}`
    : `${Routes.Certificates}/${certificate.id}`;

  const instalmentProgressProps = useMemo(() => {
    if (!isInstalmentWalletPaymentType(certificate.paymentType)) {
      return null;
    }
    return resolveInstallmentProgressProps({
      certificate,
      instalmentSchedule: fetchedInstallmentSchedule,
      intl,
      totalPriceFromInstallmentsResponse: fetchedCertificateTotalPrice,
    });
  }, [certificate, fetchedCertificateTotalPrice, fetchedInstallmentSchedule, intl]);

  const showExpiryOnFullPaymentNote: boolean = shouldShowExpiryOnFullPaymentNote({
    expiresText,
    paymentType: certificate.paymentType,
    status: certificate.status,
  });

  const shouldShowUpdatePaymentCard: boolean = useMemo(
    () => shouldShowWalletUpdatePaymentCard(certificate),
    [certificate.paymentType, certificate.status],
  );

  const handleDetailsClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.stopPropagation();
      dispatch(walletDrawerActions.setWalletDrawerOpen(false));
    },
    [dispatch],
  );

  const handleToggleExpanded = useCallback((): void => {
    setIsExpanded((prev) => !prev);
  }, []);

  const isSomeFailedInstallment: boolean = useMemo(() => {
    return !!fetchedInstallmentSchedule?.instalments.some(
      (instalment) => instalment.status === 'failed',
    );
  }, [fetchedInstallmentSchedule]);

  const isFailedInstallmentLast: boolean = useMemo(() => {
    if (!fetchedInstallmentSchedule) {
      return false;
    }
    const lastInstalment =
      fetchedInstallmentSchedule.instalments[fetchedInstallmentSchedule.instalments.length - 1];
    return lastInstalment.status === 'failed';
  }, [fetchedInstallmentSchedule]);

  const handleAfterCardUpdated = useCallback((): void => {
    if (isFailedInstallmentLast) {
      fetchCertificates();
    } else if (isSomeFailedInstallment) {
      void refetchInstallments();
    }
  }, [refetchInstallments, isSomeFailedInstallment, isFailedInstallmentLast, fetchCertificates]);

  const showInstallmentSkeleton: boolean =
    isExpanded && shouldFetchInstallments && isInstallmentsLoading;

  const showInstallmentProgress: boolean =
    isExpanded &&
    shouldFetchInstallments &&
    !isInstallmentsLoading &&
    instalmentProgressProps !== null;

  const showInstallmentsPanel: boolean =
    isExpanded &&
    !showInstallmentSkeleton &&
    (showInstallmentProgress || shouldShowUpdatePaymentCard);

  return (
    <article className={styles.card}>
      <button className={styles.header} type="button" onClick={handleToggleExpanded}>
        <div className={styles.titleBlock}>
          <div className={styles.titleRow}>
            <p className={styles.title}>{certificate.name}</p>
            <div className={styles.statusRow}>
              <span
                className={`${styles.badge} ${
                  styles[getWalletStatusClassName(certificate.status)]
                }`}
              >
                {getWalletStatusLabel(certificate.status, intl)}
              </span>
              <span className={styles.chevron}>
                <img
                  src={ChevronIcon}
                  alt=""
                  aria-hidden
                  className={`${styles.chevronIcon} ${isExpanded ? styles.expanded : ''}`}
                />
              </span>
            </div>
          </div>
          <p className={styles.code}>{certificate.code}</p>
          <div className={styles.dateRowWrap}>
            <div className={styles.dateRow}>
              {!!purchasedText && (
                <span>
                  <FormattedMessage id="certificates.wallet.purchased" /> <b>{purchasedText}</b>
                </span>
              )}
              {!!expiresText && (
                <span>
                  <FormattedMessage id="certificates.wallet.expires" /> <b>{expiresText}</b>
                </span>
              )}
              {showExpiryOnFullPaymentNote && (
                <span className={styles.expiryOnFullPayment}>
                  <FormattedMessage id="certificates.wallet.expiryOnFullPayment" />
                </span>
              )}
            </div>
            <Link to={detailsPath} className={styles.detailsLink} onClick={handleDetailsClick}>
              <FormattedMessage id="certificates.wallet.seeDetails" />
            </Link>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className={styles.expandedBody}>
          <div className={styles.items}>
            {certificate.items.map((item) => (
              <div key={item.id} className={styles.itemRow}>
                <WalletItem
                  item={item}
                  accountUsername={accountUsername}
                  certificateCode={certificate.code}
                  certificateTotalPrice={certificate.totalPrice}
                  certificateStatus={certificate.status}
                  certificateExpiresAt={certificate.expiresAt}
                  certificateUpdatedAt={certificate.updatedAt}
                />
              </div>
            ))}
          </div>
          {showInstallmentSkeleton && (
            <Skeleton active paragraph={{ rows: 4 }} title={{ width: '50%' }} />
          )}
          {showInstallmentsPanel && (
            <InstallmentProgress
              progress={
                showInstallmentProgress && instalmentProgressProps !== null
                  ? instalmentProgressProps
                  : null
              }
              footer={
                shouldShowUpdatePaymentCard ? (
                  <Suspense
                    fallback={<Skeleton active paragraph={{ rows: 2 }} title={{ width: '40%' }} />}
                  >
                    <WalletCardUpdatePaymentBlockLazy
                      certificateId={certificate.id}
                      certificateName={certificate.name}
                      isWalletExpanded={isExpanded}
                      onCardUpdated={handleAfterCardUpdated}
                    />
                  </Suspense>
                ) : undefined
              }
            />
          )}
        </div>
      )}
    </article>
  );
};
